import { TimeHelper } from "../../Everou/_helpers/TimeHelper";

export interface PMSIntegrationCreateServer {
    uid: string,
    rooms: string[];
    host_id?: string|number;
    host_name?:string;
    room_id?:number;
    room_name?:string;
    link?:string;
    handler_url?:string;
}

// Partial update of an existing location integration. Keyed on
// `integration_uid` (the location-integration's own uid, same one DELETE uses),
// as opposed to the create payload which is keyed on the template `uid`. Only
// the fields present are overwritten server-side; omitted ones are preserved.
export interface PMSIntegrationUpdateServer {
    integration_uid: string;
    // Move the integration to another IntegrationTemplate: the row is
    // re-credentialed from it, since that template is what will be propagating
    // to it from then on. `rooms` must travel along -- the backend always takes
    // it as the full assignment -- so send the ones it already has.
    template_uid?: string;
    rooms?: string[];
    host_id?: string|number;
    host_name?: string;
    room_id?: number;
    room_name?: string;
    link?: string;
}

// Host-facing reservation state, computed by the backend and sent as a string.
// Precedence applied server-side: cancelled > expired > early_checkout >
// active > upcoming.
export enum PMSReservationStatus {
    UPCOMING = "upcoming",
    ACTIVE = "active",
    EARLY_CHECKOUT = "early_checkout",
    EXPIRED = "expired",
    CANCELLED = "cancelled",
}

export interface PMSServerReservation {
    uid: string;
    status: PMSReservationStatus;
    reservation_id: string;
    email: string;
    guest_name: string;
    reservation_start: Date;
    reservation_end: Date;
    early_checkout_at: Date | null;
    early_checkin_at: Date | null;
    early_checkin_offered_at: Date | null;
    early_checkin_confirmed_at: Date | null;
    early_checkin_rejected_at: Date | null;
    invitation_uid: string | null;
    keypad_code: string | null;
    created_on: Date;
    modified_on: Date;
    integration: number;
    url: string;
    location_name: string | null;
    location_uid: string | null;
    is_manual: boolean;
}

export interface PMSReservation extends Omit<PMSServerReservation, 'created_on' | 'modified_on' | 'reservation_id' | 'reservation_start' | 'reservation_end' | 'early_checkout_at' | 'early_checkin_at' | 'early_checkin_offered_at' | 'early_checkin_confirmed_at' | 'early_checkin_rejected_at' | 'invitation_uid' | 'keypad_code' | 'location_name' | 'location_uid' | 'is_manual' | 'guest_name'>{
    reservationId: string;
    guestName: string;
    reservationStart: Date;
    reservationEnd: Date;
    earlyCheckoutAt: Date | null;
    earlyCheckinAt: Date | null;
    earlyCheckinOfferedAt: Date | null;
    earlyCheckinConfirmedAt: Date | null;
    earlyCheckinRejectedAt: Date | null;
    invitationUid: string | null;
    keypadCode: string | null;
    createdOn: Date;
    modifiedOn: Date;
    locationName: string | null;
    locationUid: string | null;
    isManual: boolean;
}

// Shared badge styling (i18n key + color) for each backend status, so every
// place that shows a reservation renders the same distintivo.
export const RESERVATION_STATE_BADGE: Record<PMSReservationStatus, { labelKey: string; color: string }> = {
    [PMSReservationStatus.UPCOMING]: {
        labelKey: "mod_pms_reservation_state_upcoming",
        color: "#009FDF",
    },
    [PMSReservationStatus.ACTIVE]: {
        labelKey: "mod_pms_reservation_state_active",
        color: "#27ae60",
    },
    [PMSReservationStatus.EARLY_CHECKOUT]: {
        labelKey: "mod_pms_reservation_state_early_checkout",
        color: "#f39c12",
    },
    [PMSReservationStatus.EXPIRED]: {
        labelKey: "mod_pms_reservation_state_expired",
        color: "#9b9b9b",
    },
    [PMSReservationStatus.CANCELLED]: {
        labelKey: "mod_integrations_reservation_cancelled",
        color: "#e57373",
    },
};

// Whether to show the "flat is ready" early check-in offer control for a
// reservation. Once the guest has picked an arrival time (earlyCheckinAt) the
// time is shown instead. The control shows when the reservation has an
// invitation and either the offer is already active (withdrawing is always
// allowed) or the location has early check-in on and the stay hasn't started.
// `offered` is passed in so each view can source it from its own optimistic
// state; `locationEnabled` likewise (global list vs. selected-location info).
export function canOfferEarlyCheckin(
    reservation: PMSReservation,
    { offered, locationEnabled }: { offered: boolean; locationEnabled: boolean },
): boolean {
    if (reservation.earlyCheckinAt) return false;
    if (!reservation.invitationUid) return false;

    const startIso = TimeHelper.serverStringToIsoString(reservation.reservationStart);
    const beforeStart = !!startIso && !TimeHelper.isBeforeNowUtc(startIso);
    return offered || (locationEnabled && beforeStart);
}

// Host-facing state of the guest's early check-in request. "expired" is
// derived, not stored: nobody answered and the reservation already started,
// so the request lapsed (the guest just enters at the normal hour).
// Confirmed/rejected persist as history after the start.
export type EarlyCheckinRequestState =
    | "requested"
    | "confirmed"
    | "rejected"
    | "expired";

// Shared badge styling per request state, so both reservation views render
// the same pill. `titleKey` is the long form used as the hover title.
export const EARLY_CHECKIN_STATE_BADGE: Record<
    EarlyCheckinRequestState,
    { labelKey: string; titleKey: string; color: string }
> = {
    requested: {
        labelKey: "mod_pms_reservation_early_checkin_requested_badge",
        titleKey: "mod_pms_reservation_early_checkin_requested",
        color: "#f39c12",
    },
    confirmed: {
        labelKey: "mod_pms_reservation_early_checkin_confirmed_badge",
        titleKey: "mod_pms_reservation_early_checkin_confirmed",
        color: "#27ae60",
    },
    rejected: {
        labelKey: "mod_pms_reservation_early_checkin_rejected_badge",
        titleKey: "mod_pms_reservation_early_checkin_rejected",
        color: "#e57373",
    },
    expired: {
        labelKey: "mod_pms_reservation_early_checkin_expired_badge",
        titleKey: "mod_pms_reservation_early_checkin_expired",
        color: "#9b9b9b",
    },
};

// Single source for the request state and whether the host can still act on
// it. State is null (show just the plain time) when the guest hasn't
// requested a time or the reservation has no invitation — without the
// invitation uid the decision endpoint can't be called, so a state badge
// would be an unresolvable pending task. `canDecide` gates the
// confirm/reject/undo controls: once the reservation starts the normal
// access window is already open, so deciding no longer does anything useful
// and only the state badge remains. Note canDecide is NOT derivable from the
// state alone — a confirmed reservation keeps its badge after the start but
// must lose its undo control. `confirmedAt`/`rejectedAt` are passed in so
// each view can source them from its own optimistic state.
export function getEarlyCheckinDecision(
    reservation: PMSReservation,
    {
        confirmedAt,
        rejectedAt,
    }: { confirmedAt: Date | string | null; rejectedAt: Date | string | null },
): { state: EarlyCheckinRequestState | null; canDecide: boolean } {
    if (!reservation.earlyCheckinAt || !reservation.invitationUid)
        return { state: null, canDecide: false };

    const startIso = TimeHelper.serverStringToIsoString(reservation.reservationStart);
    const started = !!startIso && TimeHelper.isBeforeNowUtc(startIso);

    const state: EarlyCheckinRequestState = confirmedAt
        ? "confirmed"
        : rejectedAt
        ? "rejected"
        : started
        ? "expired"
        : "requested";
    return { state, canDecide: !!startIso && !started };
}

// The guest's public link only shows the early check-in card on the arrival
// day, so an offer activated earlier is stored but not visible to the guest
// yet. True when the reservation starts on a later day than today in the
// given timezone, so views can tell the host the offer is not live yet.
export function earlyCheckinOfferNotVisibleYet(
    reservation: PMSReservation,
    timezone: string,
): boolean {
    const startIso = TimeHelper.serverStringToIsoString(reservation.reservationStart);
    if (!startIso) return false;

    const DAY_FORMAT = "YYYY-MM-DD";
    const startDay = TimeHelper.localizeIsoStringToFormat(startIso, timezone, DAY_FORMAT);
    const today = TimeHelper.localizeIsoStringToFormat(
        new Date().toISOString(),
        timezone,
        DAY_FORMAT,
    );
    return startDay > today;
}

export function getReservationStateBadge(reservation: PMSReservation) {
    return (
        RESERVATION_STATE_BADGE[reservation.status] ??
        RESERVATION_STATE_BADGE[PMSReservationStatus.UPCOMING]
    );
}